import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Download, FileText, Undo2 } from "lucide-react";
import { api, apiBlob, saveBlob } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { currency } from "@/lib/format";
import type { Booking } from "@/types/domain";

// Invoice of a booking. Before the final check-out it is a proforma (no number, follows the bill); at check-out
// the numbered invoice is issued and never changes. Money given back afterwards goes out as a credit note.
// A business guest's company name and GSTIN (B2B) are printed on it - they can only change before it is issued.

type DocumentSummary = { id: string; number: string; issuedAt: string; total: number; reason?: string | null };
type TaxGroup = { rate: number; taxable: number; cgst: number; sgst: number; tax: number };
type InvoiceView = {
  proforma: boolean;
  invoice: DocumentSummary | null;
  creditNotes: DocumentSummary[];
  creditable: number;
  bill: { title: string; gstInclusive: boolean; seller: { gstin: string | null }; taxGroups: TaxGroup[]; taxable: number; tax: number; total: number };
};

const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const fileName = (value: string) => `${value.replace(/[^A-Za-z0-9-]+/g, "-")}.pdf`;

export function InvoicePanel({ booking, canEditBilling, canCredit, onUpdated }: {
  booking: Booking;
  canEditBilling: boolean;
  canCredit: boolean;
  onUpdated: (booking: Booking) => void;
}) {
  const queryClient = useQueryClient();
  const key = ["booking-invoice", booking.id, booking.version ?? 0, booking.bookingStatus];
  const view = useQuery({ queryKey: key, queryFn: () => api<InvoiceView>(`/bookings/${booking.id}/invoice`) });
  const [editingBilling, setEditingBilling] = useState(false);
  const [crediting, setCrediting] = useState(false);

  const download = useMutation({
    mutationFn: async (document?: DocumentSummary) => {
      const blob = await apiBlob(`/bookings/${booking.id}/invoice/pdf${document ? `?documentId=${document.id}` : ""}`);
      saveBlob(blob, fileName(document?.number ?? view.data?.invoice?.number ?? `proforma-${booking.bookingRef}`));
    },
  });

  const data = view.data;
  const issued = Boolean(data?.invoice);
  const registered = Boolean(data?.bill.seller.gstin);

  return (
    <section className="booking-line-section invoice-panel">
      <div className="booking-line-section__head">
        <h4><FileText size={16} /> {issued ? `Invoice ${data!.invoice!.number}` : "Invoice"}</h4>
        <span className="invoice-panel__actions">
          {canEditBilling && !issued && !editingBilling && (
            <button type="button" className="booking-line-section__add" onClick={() => setEditingBilling(true)}><Building2 size={13} /> {booking.billingGstin ? "Edit company" : "Business guest (GSTIN)"}</button>
          )}
          <Button variant="secondary" loading={download.isPending} disabled={!data} onClick={() => download.mutate(undefined)}>
            <Download size={15} /> {issued ? "Download" : "Proforma"}
          </Button>
        </span>
      </div>
      {view.isLoading && <p className="booking-line-section__empty">Loading...</p>}
      {view.error && <div className="form-error">{view.error.message}</div>}
      {data && (
        <>
          <p className="invoice-panel__hint">
            {issued
              ? `Issued ${new Date(data.invoice!.issuedAt).toLocaleDateString("en-IN")} for ${currency.format(data.invoice!.total)}.`
              : "The numbered invoice is issued automatically at the final check-out."}
            {registered ? (data.bill.gstInclusive ? " Room prices include GST." : " GST is added on top of the room price.") : " The property has no GSTIN - no GST on rooms."}
          </p>
          {registered && data.bill.taxGroups.some((group) => group.tax > 0) && (
            <div className="invoice-panel__tax">
              {data.bill.taxGroups.filter((group) => group.tax > 0).map((group) => (
                <div key={group.rate}><span>GST {group.rate}% on {currency.format(group.taxable)}</span><strong className="num">CGST {currency.format(group.cgst)} + SGST {currency.format(group.sgst)}</strong></div>
              ))}
            </div>
          )}
          {booking.billingGstin && !editingBilling && (
            <p className="invoice-panel__hint"><Building2 size={13} /> Bill to <strong>{booking.billingName}</strong> · GSTIN {booking.billingGstin}{booking.billingAddress ? ` · ${booking.billingAddress}` : ""}</p>
          )}
          {editingBilling && <BillingForm booking={booking} onCancel={() => setEditingBilling(false)} onSaved={(updated) => { setEditingBilling(false); onUpdated(updated); }} />}

          {data.creditNotes.length > 0 && (
            <div className="invoice-panel__notes">
              {data.creditNotes.map((note) => (
                <div key={note.id} className="invoice-panel__note">
                  <div><strong>Credit note {note.number}</strong><small>{currency.format(note.total)} · {note.reason}</small></div>
                  <button type="button" className="icon-button" aria-label={`Download ${note.number}`} onClick={() => download.mutate(note)}><Download size={15} /></button>
                </div>
              ))}
            </div>
          )}
          {issued && canCredit && data.creditable > 0 && !crediting && (
            <button type="button" className="booking-line-section__add" onClick={() => setCrediting(true)}><Undo2 size={13} /> Give money back (credit note)</button>
          )}
          {crediting && (
            <CreditNoteForm
              booking={booking}
              max={data.creditable}
              onCancel={() => setCrediting(false)}
              onDone={(updated) => { setCrediting(false); onUpdated(updated); void queryClient.invalidateQueries({ queryKey: ["booking-invoice", booking.id] }); }}
            />
          )}
          {download.error && <div className="form-error">{download.error.message}</div>}
        </>
      )}
    </section>
  );
}

function BillingForm({ booking, onCancel, onSaved }: { booking: Booking; onCancel: () => void; onSaved: (booking: Booking) => void }) {
  const [name, setName] = useState(booking.billingName ?? "");
  const [gstin, setGstin] = useState(booking.billingGstin ?? "");
  const [address, setAddress] = useState(booking.billingAddress ?? "");
  const save = useMutation({
    mutationFn: () => api<Booking>(`/bookings/${booking.id}/billing`, { method: "PATCH", body: { billingName: name.trim(), billingGstin: gstin.trim().toUpperCase(), billingAddress: address.trim() } }),
    onSuccess: onSaved,
  });
  const gstinValid = !gstin.trim() || GSTIN_PATTERN.test(gstin.trim().toUpperCase());
  return (
    <div className="booking-inline-form">
      <h4>Business guest - printed on the invoice</h4>
      <label>Company name<input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Acme Travels Pvt Ltd" /></label>
      <label>GSTIN<input value={gstin} maxLength={15} style={{ textTransform: "uppercase" }} onChange={(event) => setGstin(event.target.value)} placeholder="15 characters" /></label>
      {!gstinValid && <div className="form-error">Enter a valid 15-character GSTIN</div>}
      <label>Billing address<input value={address} onChange={(event) => setAddress(event.target.value)} /></label>
      {save.error && <div className="form-error">{save.error.message}</div>}
      <div className="bd-actions__row">
        <Button variant="secondary" disabled={save.isPending} onClick={onCancel}>Cancel</Button>
        <Button loading={save.isPending} disabled={!gstinValid || (Boolean(gstin.trim()) && !name.trim())} onClick={() => save.mutate()}>Save</Button>
      </div>
    </div>
  );
}

function CreditNoteForm({ booking, max, onCancel, onDone }: { booking: Booking; max: number; onCancel: () => void; onDone: (booking: Booking) => void }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const issue = useMutation({
    mutationFn: () => api<{ booking: Booking }>(`/bookings/${booking.id}/credit-notes`, { method: "POST", body: { amount: Number(amount), reason: reason.trim() } }),
    onSuccess: (result) => onDone(result.booking),
  });
  const value = Number(amount);
  const valid = value > 0 && value <= max + 0.005 && reason.trim().length >= 3;
  return (
    <div className="booking-inline-form">
      <h4>Credit note</h4>
      <p className="booking-inline-form__hint">For money given back after the invoice (GST included in the amount). Up to {currency.format(max)}. Record the refund after this.</p>
      <label>Amount<input type="number" min="0" step="0.01" max={max} value={amount} onChange={(event) => { setAmount(event.target.value); setConfirming(false); }} /></label>
      <label>Reason<input value={reason} onChange={(event) => { setReason(event.target.value); setConfirming(false); }} placeholder="e.g. AC not working one night" /></label>
      {confirming && <div className="form-warning">Issue a credit note for {currency.format(value)}? It gets its own number and can't be undone.</div>}
      {issue.error && <div className="form-error">{issue.error.message}</div>}
      <div className="bd-actions__row">
        <Button variant="secondary" disabled={issue.isPending} onClick={onCancel}>Cancel</Button>
        {confirming
          ? <Button variant="danger" loading={issue.isPending} onClick={() => issue.mutate()}>Yes, issue credit note</Button>
          : <Button disabled={!valid} onClick={() => setConfirming(true)}>Continue</Button>}
      </div>
    </div>
  );
}
